/**
 * The list pages of the zoning plan module (app/UI/Modules/ZoningPlan). Their filters are in the address, so a
 * change of a filter loads the page anew; the history of naja is off in the application.
 */
export function registerZoningPlanPages(snippet: HTMLElement): void {
    snippet.querySelectorAll<HTMLElement>('[data-zoning-plan-region-views]').forEach(views => {
        if (views.dataset.zoningPlanRegionViewsInitialized === '1') {
            return;
        }
        views.dataset.zoningPlanRegionViewsInitialized = '1';

        showRegionView(snippet, regionView(readStoredRegionView()));
        views.querySelectorAll<HTMLElement>('[data-zoning-plan-region-view]').forEach(button => {
            button.addEventListener('click', () => {
                const view = regionView(button.dataset.zoningPlanRegionView ?? null);
                showRegionView(snippet, view);
                storeRegionView(view);
            });
        });
    });

    snippet.querySelectorAll<HTMLFormElement>('form[data-zoning-plan-filters]').forEach(form => {
        if (form.dataset.zoningPlanFiltersInitialized === '1') {
            return;
        }
        form.dataset.zoningPlanFiltersInitialized = '1';

        form.addEventListener('change', (event) => {
            if (keepsOneChecked(form, event.target)) {
                form.submit();
            }
        });
    });
}

/**
 * A group of checkboxes (data-zoning-plan-filter-group) keeps at least one checked: without any the page would show
 * its default choice, not what the user unchecked.
 */
function keepsOneChecked(form: HTMLFormElement, target: EventTarget | null): boolean {
    if (!(target instanceof HTMLInputElement) || target.dataset.zoningPlanFilterGroup === undefined || target.checked) {
        return true;
    }

    const group = target.dataset.zoningPlanFilterGroup;
    const checked = Array.from(form.querySelectorAll<HTMLInputElement>('input[data-zoning-plan-filter-group]'))
        .some((input) => input.dataset.zoningPlanFilterGroup === group && input.checked);
    if (!checked) {
        target.checked = true;
    }

    return checked;
}

export type RegionView = 'tiles' | 'map';

const REGION_VIEW_KEY = 'zoningPlan.regionView';

/**
 * The regions of the page of towns as tiles (the default, they fit a phone) or as a map.
 */
export function regionView(value: string | null): RegionView {
    return value === 'map' ? 'map' : 'tiles';
}

function showRegionView(snippet: HTMLElement, view: RegionView): void {
    snippet.querySelectorAll<HTMLElement>('[data-zoning-plan-region-pane]').forEach(pane => {
        pane.classList.toggle('d-none', pane.dataset.zoningPlanRegionPane !== view);
    });
    snippet.querySelectorAll<HTMLElement>('[data-zoning-plan-region-view]').forEach(button => {
        const active = button.dataset.zoningPlanRegionView === view;
        button.classList.toggle('fw-semibold', active);
        button.classList.toggle('text-dark', active);
        button.classList.toggle('text-muted', !active);
    });
}

// The choice of the viewer only; without the storage (a private window) the tiles are shown.
function readStoredRegionView(): string | null {
    try {
        return window.localStorage.getItem(REGION_VIEW_KEY);
    } catch {
        return null;
    }
}

function storeRegionView(view: RegionView): void {
    try {
        window.localStorage.setItem(REGION_VIEW_KEY, view);
    } catch {
        // The choice is not kept; nothing else depends on it.
    }
}
